import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown, Building2, Loader2, Search } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/cn';

export function SiteSwitcher() {
  const { user, switchSite } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) { setSearch(''); return; }
    setTimeout(() => searchRef.current?.focus(), 0);
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener('click', onClick);
    return () => window.removeEventListener('click', onClick);
  }, [open]);

  if (!user || user.allowedSites.length <= 1) return null;

  const siteLabel = (siteId: string) =>
    user.siteNames?.[siteId] || `Site ${user.allowedSites.indexOf(siteId) + 1}`;

  async function pick(siteId: string) {
    if (siteId === user!.activeSiteId) {
      setOpen(false);
      return;
    }
    setSwitching(siteId);
    try {
      // switchSite() itself clears the query cache — every page that loads
      // next fetches fresh data for the new site.
      await switchSite(siteId);
      setOpen(false);
      navigate('/dashboard', { replace: true });
    } finally {
      setSwitching(null);
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-md border border-surface-border bg-surface-base px-3 py-1.5 text-sm text-ink-secondary hover:bg-surface-muted"
      >
        <Building2 className="h-4 w-4" />
        <span className="font-medium text-ink-primary">{siteLabel(user.activeSiteId)}</span>
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute right-0 z-30 mt-1 min-w-[260px] rounded-md border border-surface-border bg-surface-base shadow-elevated">
          <div className="border-b border-surface-border px-3 py-2 text-xs uppercase tracking-wide text-ink-muted">
            Switch site
          </div>
          <div className="border-b border-surface-border px-2 py-2">
            <div className="flex items-center gap-1.5 rounded border border-surface-border bg-surface-muted px-2 py-1.5">
              <Search className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search sites…"
                className="w-full bg-transparent text-xs text-ink-primary placeholder:text-ink-muted outline-none"
              />
            </div>
          </div>
          <div className="max-h-[260px] overflow-y-auto py-1">
            {(() => {
              const q = search.trim().toLowerCase();
              const filtered = user.allowedSites.filter((id) =>
                !q || siteLabel(id).toLowerCase().includes(q),
              );
              if (!filtered.length) {
                return (
                  <p className="px-3 py-4 text-center text-xs text-ink-muted">No sites match.</p>
                );
              }
              return filtered.map((siteId) => {
                const active = siteId === user.activeSiteId;
                const isSwitching = switching === siteId;
                return (
                  <button
                    key={siteId}
                    type="button"
                    onClick={() => pick(siteId)}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2.5 text-sm transition-colors',
                      active ? 'bg-brand-50 text-brand-700' : 'text-ink-secondary hover:bg-surface-muted',
                    )}
                    disabled={isSwitching}
                  >
                    <Building2 className="h-4 w-4 shrink-0" />
                    <span className="flex-1 text-left">
                      <span className="block font-medium">{siteLabel(siteId)}</span>
                    </span>
                    {isSwitching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : active && <Check className="h-3.5 w-3.5" />}
                  </button>
                );
              });
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
